import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput } from '@msdyn365-commerce/core';
import { addLocaleInformation } from '@msdyn365-commerce/action-internal';
import { AsyncResult, CommerceList, IDataServiceRequest, retailAction } from '@msdyn365-commerce/retail-proxy';
import {
    createCommerceListAsync,
    createGetByCustomerInput
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { reaction, toJS } from 'mobx';

interface CustomerWishlistOperations {
    creation: Promise<CommerceList> | undefined;
    refresh: Promise<CommerceList[]> | undefined;
}

class CustomerWishlistOperationsInput implements IActionInput {
    private readonly cacheKey: string;

    constructor(context: IActionContext) {
        this.cacheKey = JSON.stringify([
            context.requestContext.user.customerAccountNumber,
            context.requestContext.apiSettings.channelId,
            context.requestContext.locale
        ]);
    }

    public getCacheKey = (): string => this.cacheKey;

    public getCacheObjectType = (): string => 'CustomerWishlistOperations';

    public dataCacheType = (): CacheType => 'request';
}

const getCustomerWishlistOperations = (context: IActionContext): CustomerWishlistOperations => {
    const input = new CustomerWishlistOperationsInput(context);
    const current = context.get<CustomerWishlistOperations>(input.getCacheObjectType(), input.getCacheKey());
    if (current && !Array.isArray(current)) {
        return current;
    }

    context.update(input, { creation: undefined, refresh: undefined });
    const operations = context.get<CustomerWishlistOperations>(input.getCacheObjectType(), input.getCacheKey());
    if (!operations || Array.isArray(operations)) {
        throw new Error('[get-customer-wishlist] Unable to coordinate wishlist operations');
    }
    return operations;
};

// Shares the request cache entry that wishlist add/remove calls update via actionContext.update(createGetByCustomerInput(...)).
export const createCustomerWishlistInput = (): IDataServiceRequest => createGetByCustomerInput({}, null);

export class CachedCustomerWishlistInput implements IActionInput {
    public readonly request: IDataServiceRequest;

    constructor(locale: string) {
        this.request = createCustomerWishlistInput();
        addLocaleInformation(this.request, locale);
    }

    public getCacheKey = (): string => this.request.getCacheKey();

    public getCacheObjectType = (): string => this.request.getCacheObjectType();

    public dataCacheType = (): CacheType => 'request';
}

export const createCachedCustomerWishlistInput = (inputData: { requestContext: { locale: string } }): CachedCustomerWishlistInput =>
    new CachedCustomerWishlistInput(inputData.requestContext.locale);

export const getCurrentCustomerWishlist = (wishlists: AsyncResult<CommerceList[]> | undefined): CommerceList | undefined =>
    wishlists?.result?.length ? wishlists.result[0] : undefined;

export const publishCustomerWishlist = (context: IActionContext, wishlist: CommerceList): void => {
    context.update(createCustomerWishlistInput(), [wishlist]);
};

export const getOrCreateCustomerWishlist = (
    context: IActionContext,
    wishlists: AsyncResult<CommerceList[]> | undefined
): Promise<CommerceList> => {
    const { user } = context.requestContext;
    if (typeof window === 'undefined' || !user.isAuthenticated || !user.customerAccountNumber || wishlists?.status !== 'SUCCESS') {
        return Promise.reject(new Error('[get-customer-wishlist] Authenticated wishlist data must be ready before adding items'));
    }

    const wishlist = getCurrentCustomerWishlist(wishlists);
    if (wishlist) {
        return Promise.resolve(wishlist);
    }

    const operations = getCustomerWishlistOperations(context);
    if (!operations.creation) {
        operations.creation = Promise.resolve(
            createCommerceListAsync(
                { callerContext: context },
                { CustomerId: user.customerAccountNumber, Name: 'Wishlist', CommerceListTypeValue: 1, Id: 0 }
            )
        )
            .then(created => {
                publishCustomerWishlist(context, created);
                return created;
            })
            .finally(() => {
                operations.creation = undefined;
            });
    }
    return operations.creation;
};

export async function getCustomerWishlist(
    input: CachedCustomerWishlistInput | IDataServiceRequest,
    actionContext: IActionContext
): Promise<CommerceList[]> {
    if (!actionContext.requestContext.user.isAuthenticated) {
        return [];
    }

    return retailAction<CommerceList[]>('request' in input ? input.request : input, actionContext);
}

export const refreshCustomerWishlist = (
    context: IActionContext,
    wishlists: AsyncResult<CommerceList[]> | undefined
): Promise<CommerceList[]> => {
    if (typeof window === 'undefined' || !context.requestContext.user.isAuthenticated) {
        return Promise.resolve([]);
    }

    const operations = getCustomerWishlistOperations(context);
    if (!operations.refresh) {
        let hasNewerResult = false;
        const dispose = reaction(
            () => toJS(wishlists?.result),
            () => {
                hasNewerResult = true;
            }
        );
        const input = createCustomerWishlistInput();
        input.overrideCacheType('get');

        // Direct execution keeps a late network response from replacing newer cache data before the freshness check.
        operations.refresh = getCustomerWishlist(input, context)
            .then(result => {
                if (hasNewerResult) {
                    return wishlists?.result ?? result;
                }
                context.update(createCustomerWishlistInput(), result);
                return result;
            })
            .finally(() => {
                dispose();
                operations.refresh = undefined;
            });
    }
    return operations.refresh;
};

export const getCustomerWishlistDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/custom/get-customer-wishlist',
    action: <IAction<CommerceList[]>>getCustomerWishlist,
    input: createCachedCustomerWishlistInput
});

export default getCustomerWishlistDataAction;
